import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { CurrencyCodeSchema, MoneySchema } from '../primitives/money';
import { ReasonCodeSchema } from '../reason-code';

/**
 * Body of POST /trading-account (FXM-01 §4; C1 spec §5.2 decision 4, R11).
 * Provisional, Q38: if the password goes, `password` leaves the request. ADR 0008: the contract keeps
 * MT4 so it can come back, but the API answers 400 VALIDATION_FAILED to it (S6 R33) and E4 sends MT5.
 */
export const ConnectTradingAccountRequestSchema = z.strictObject({
  platform: z
    .enum(['MT4', 'MT5'])
    .describe(
      'Required, no default (R11). E4 always sends MT5; MT4 answers 400 VALIDATION_FAILED (R33).',
    ),
  accountNumber: z
    .string()
    .regex(/^\d{1,20}$/)
    .describe('MT account number. Never returned by any route (FXM-01 §7).'),
  password: z
    .string()
    .min(1)
    .max(128)
    .describe(
      'MT trading password. Forwarded to the verifier only; never stored by the API, echoed or logged (I9).',
    ),
  server: z.string().min(1).max(128).describe('Broker server name, as shown in MetaTrader.'),
});
/** POST /trading-account body. */
export type ConnectTradingAccountRequest = z.infer<typeof ConnectTradingAccountRequestSchema>;

/** Why a verification failed (FXM-01 §5, §6 cases 5 and 7). */
export const TradingAccountRejectionCodeSchema = ReasonCodeSchema.extract([
  'TRADING_ACCOUNT_CREDENTIALS_REJECTED',
  'TRADING_ACCOUNT_SERVER_NOT_FOUND',
  'TRADING_ACCOUNT_READ_ONLY',
  'TRADING_ACCOUNT_LINKED_TO_OTHER_TRADER',
  // P1 R2 (Q264, provisional): a netting or exchange margin mode (an unreadable one is a 503).
  'TRADING_ACCOUNT_NETTING_NOT_SUPPORTED',
]);

/** Warnings on an accepted account (FXM-01 §6 case 11: balance too low is accepted with a warning). */
export const TradingAccountWarningCodeSchema = ReasonCodeSchema.extract([
  'TRADING_ACCOUNT_BALANCE_LOW',
]);

/** Consent as stored on the account link: text version and server timestamp (FXM-01 §7, S6 R14). */
const AccountConsentSchema = z.strictObject({
  acceptedAt: IsoDateTimeSchema,
  consentTextVersion: z.string().min(1),
});

/**
 * Summary shared by `VERIFIED`, `OPERATIONAL` and `UNREACHABLE` (C1 spec §5.2, decision 4, R17).
 * There is no account number, login, phone or password: `accountLast4` is the only account
 * identifier (FXM-01 §7, I9, R12). `balance` and `consent` differ per state, so each state adds them.
 */
const accountSummary = {
  platform: z
    .enum(['MT4', 'MT5'])
    .describe('Platform of the account. FXMate is MT5 only (ADR 0008); MT4 stays in the contract.'),
  server: z.string().min(1).max(128),
  /**
   * Provisional, Q81: last digits of the account number, shown as `••••4567` (S6 R18). Removed if
   * Julien answers "not at all"; what the API stores is Q96.
   */
  accountLast4: z
    .string()
    .regex(/^\d{1,4}$/)
    .describe(
      'Provisional (Q81): last 1 to 4 digits of the account number. Never the full number.',
    ),
  holderName: z.string().min(1).max(128),
  currency: CurrencyCodeSchema.describe('Account currency (S6 R6).'),
  isDemo: z.boolean().describe('Shown as a prominent demo badge (FXM-01 §6 case 4).'),
  warnings: z.array(TradingAccountWarningCodeSchema),
};

/**
 * State of the trader's single trading account (FXM-01 §4, §5; C1 spec §5.2 decision 4).
 * `VERIFIED` = verified, no consent yet; `OPERATIONAL` = verified, consented and reachable
 * (data model §2.5). Provisional, Q83: `/me` does not count `VERIFIED` as connected.
 * Strict: an unknown `status` fails the parse (R16, AC25).
 */
export const TradingAccountSchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('NONE') }),
  z.strictObject({ status: z.literal('VERIFYING') }),
  z.strictObject({ status: z.literal('REJECTED'), reasonCode: TradingAccountRejectionCodeSchema }),
  z.strictObject({
    status: z.literal('VERIFIED'),
    ...accountSummary,
    balance: MoneySchema.describe('Balance in the account currency.'),
    consent: z.null().describe('No consent yet: the trader has not authorized FXMate (S6 R14).'),
  }),
  z.strictObject({
    status: z.literal('OPERATIONAL'),
    ...accountSummary,
    balance: MoneySchema.describe('Balance in the account currency.'),
    consent: AccountConsentSchema.describe('Operational means consented (data model §2.5, Q83).'),
  }),
  z.strictObject({
    status: z.literal('UNREACHABLE'),
    ...accountSummary,
    // Proposal, to be validated (spec §5.2): the balance is read live, so it is absent when the
    // account cannot be reached. This is the only state where it may be null.
    balance: MoneySchema.nullable().describe(
      'Proposal, to be validated: null because the balance is read live and the account is unreachable.',
    ),
    // The spec gives the shared summary `consent: {…} | null` and does not narrow it for
    // UNREACHABLE (§5.2), so both are accepted here.
    consent: AccountConsentSchema.nullable(),
    reasonCode: z.literal('TRADING_ACCOUNT_UNREACHABLE'),
  }),
]);
/** Trading account state. */
export type TradingAccount = z.infer<typeof TradingAccountSchema>;

/** Body of POST /consent (FXM-01 §7). */
export const RecordConsentRequestSchema = z.strictObject({
  consentTextVersion: z
    .string()
    .regex(/^[a-z0-9][a-z0-9.-]{0,31}$/)
    .describe(
      'Version of the FR/EN consent text the trader read. Publication of versions: Q17, Q72.',
    ),
  accepted: z
    .literal(true)
    .describe('Always true: consent is a separate explicit action, never pre-checked (I7).'),
  language: z
    .enum(['fr', 'en'])
    .describe('Language of the consent text the trader read; required, no default (S6 R14, R11).'),
});
/** POST /consent body. */
export type RecordConsentRequest = z.infer<typeof RecordConsentRequestSchema>;

/** Stored consent: text version and server timestamp (FXM-01 §7). */
export const ConsentRecordSchema = z.object({
  consentTextVersion: z.string().min(1),
  acceptedAt: IsoDateTimeSchema,
});
/** Consent record. */
export type ConsentRecord = z.infer<typeof ConsentRecordSchema>;

const tradingAccount = { name: 'TradingAccount', schema: TradingAccountSchema } as const;
const SOURCE = 'technical spec §6; FXM-01 §4, §5, §6 cases 4 to 7 and 11; US-05';
const ENGINE = 'Manager API vs MetaApi, MT5 server only, password still needed (RFX analysis §8).';

/** POST /trading-account. Verification is asynchronous: 202 VERIFYING, then poll GET. */
export const connectTradingAccountRoute = defineRoute({
  operationId: 'connectTradingAccount',
  method: 'POST',
  path: '/trading-account',
  tag: 'Trading account',
  summary: 'Start the verification of the MT account',
  description:
    'Starts the verification and returns VERIFYING; the Mini App polls GET /trading-account.',
  source: SOURCE,
  auth: 'bearer',
  provisional: { questions: ['Q12', 'Q35', 'Q38'], note: ENGINE },
  body: { name: 'ConnectTradingAccountRequest', schema: ConnectTradingAccountRequestSchema },
  success: { kind: 'json', status: 202, body: tradingAccount },
  errors: [
    { status: 409, reasonCode: 'TRADING_ACCOUNT_ALREADY_CONNECTED' },
    { status: 429, reasonCode: 'RATE_LIMITED' },
  ],
});

/** GET /trading-account. */
export const getTradingAccountRoute = defineRoute({
  operationId: 'getTradingAccount',
  method: 'GET',
  path: '/trading-account',
  tag: 'Trading account',
  summary: 'Get the state of the MT account',
  description: 'Holder, currency, balance, demo badge, or the reason of a rejection.',
  source: SOURCE,
  auth: 'bearer',
  provisional: { questions: ['Q35', 'Q38', 'Q81', 'Q83'], note: ENGINE },
  success: { kind: 'json', status: 200, body: tradingAccount },
});

/** DELETE /trading-account. */
export const disconnectTradingAccountRoute = defineRoute({
  operationId: 'disconnectTradingAccount',
  method: 'DELETE',
  path: '/trading-account',
  tag: 'Trading account',
  summary: 'Disconnect the MT account',
  description: 'Disconnects the single trading account (FXM-01 §4).',
  source: 'technical spec §6; FXM-01 §4; US-08',
  auth: 'bearer',
  provisional: { questions: ['Q35'], note: ENGINE },
  success: { kind: 'empty', status: 204 },
  errors: [{ status: 409, reasonCode: 'TRADING_ACCOUNT_NOT_CONNECTED' }],
});

/** POST /consent. */
export const recordConsentRoute = defineRoute({
  operationId: 'recordConsent',
  method: 'POST',
  path: '/consent',
  tag: 'Trading account',
  summary: 'Record the trader’s consent',
  description: 'Stores the consent text version with a server timestamp (FXM-01 §7).',
  source: 'technical spec §6; FXM-01 §7; US-05',
  auth: 'bearer',
  provisional: {
    questions: ['Q17', 'Q72', 'Q83'],
    note: 'consent text, its versioning and when the account counts as connected are open.',
  },
  body: { name: 'RecordConsentRequest', schema: RecordConsentRequestSchema },
  success: {
    kind: 'json',
    status: 201,
    body: { name: 'ConsentRecord', schema: ConsentRecordSchema },
  },
  errors: [
    // Proposal, to be validated (C1 spec §5.1 row 10): no verified account to consent on.
    { status: 409, reasonCode: 'TRADING_ACCOUNT_NOT_CONNECTED' },
    { status: 422, reasonCode: 'CONSENT_VERSION_UNKNOWN' },
  ],
});
